import { useEffect, useState, useRef } from "react";
import http, { formatApiErrorDetail } from "@/api";
import { useAuth } from "@/auth";
import { PILLARS } from "@/constants";
import { useSupplierReadonly, ReadonlyBanner } from "@/components/SupplierReadonly";
import { Upload, FileText, CheckCircle2, AlertTriangle, XCircle, Clock, ExternalLink, RefreshCw } from "lucide-react";

const STATUS_META = {
  pending: { label: "Sin subir", icon: Clock, color: "#7C7560", bg: "#EEE4CE" },
  valid: { label: "Válido", icon: CheckCircle2, color: "#0F5132", bg: "#D1F2E0" },
  expired: { label: "Vencido", icon: AlertTriangle, color: "#8A5A00", bg: "#FCEFC7" },
  invalid: { label: "No corresponde", icon: XCircle, color: "#7F1D1D", bg: "#FCD3D3" },
  under_review: { label: "En revisión", icon: Clock, color: "#1B225E", bg: "#DCE0FF" },
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyDocuments() {
  const { user } = useAuth();
  const { enabled: readonly } = useSupplierReadonly();
  const [criteria, setCriteria] = useState([]);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  const fileRefs = useRef({});

  const reload = async () => {
    const [c, d] = await Promise.all([
      http.get("/doc-criteria", { params: { active_only: true } }),
      http.get("/supplier/documents"),
    ]);
    setCriteria(c.data);
    setDocs(d.data);
  };
  useEffect(() => { reload(); }, []);

  const docFor = (criterionId) => docs.find((d) => d.criterion_id === criterionId);

  const upload = async (criterionId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(criterionId); setErr("");
    try {
      const form = new FormData();
      form.append("criterion_id", criterionId);
      form.append("file", file);
      await http.post("/supplier/documents", form, { headers: { "Content-Type": "multipart/form-data" } });
      await reload();
    } catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(null); e.target.value = ""; }
  };

  const openDoc = async (id) => {
    try {
      const res = await fetch(`${API}/documents/${id}/download`, {
        credentials: "include", // Send cookies with request
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setErr(e.message); }
  };

  // Group criteria by pillar
  const byPillar = PILLARS.reduce((acc, p) => {
    acc[p.key] = criteria.filter((c) => c.pillar_key === p.key);
    return acc;
  }, {});

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <div className="mb-6">
        <div className="font-hand text-xl text-[var(--lala-blue)]">expediente del proveedor</div>
        <h1 className="font-display text-4xl">Mis documentos</h1>
        <p className="text-[var(--ink-soft)] mt-1 max-w-2xl">
          Sube los documentos que respaldan tu evaluación. El evaluador Lala revisará y confirmará la calificación final.
        </p>
      </div>

      {err && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="mydocs-err">{err}</div>}
      {readonly && <ReadonlyBanner action="Subir y actualizar documentos" />}

      {PILLARS.map((p) => {
        const list = byPillar[p.key] || [];
        if (list.length === 0) return null;
        return (
          <div key={p.key} className="mb-7" data-testid={`mydocs-pillar-${p.key}`}>
            <h2 className="font-display text-2xl text-[var(--lala-blue)]">{p.label}</h2>
            <div className="grid md:grid-cols-2 gap-4 mt-3">
              {list.map((c) => {
                const doc = docFor(c.id);
                const status = doc?.status || "pending";
                const meta = STATUS_META[status] || STATUS_META.pending;
                const Icon = meta.icon;
                return (
                  <div key={c.id} className="sketch-border bg-[var(--cream-soft)] p-4" data-testid={`mydocs-criterion-${c.code}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-lg">{c.label}</div>
                        {c.description && <div className="text-xs text-[var(--ink-soft)] mt-0.5">{c.description}</div>}
                        {c.expected_doc_types?.length > 0 && (
                          <div className="text-[10px] uppercase tracking-wider text-[var(--lala-blue)] font-bold mt-2">
                            Acepta: {c.expected_doc_types.join(" · ")}
                          </div>
                        )}
                      </div>
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border"
                        style={{ background: meta.bg, color: meta.color, borderColor: meta.color }}
                        data-testid={`mydocs-status-${c.code}`}
                      >
                        <Icon size={12} /> {meta.label}
                      </span>
                    </div>

                    {doc ? (
                      <div className="mt-3 p-2 rounded bg-[var(--cream)] border border-[var(--line)] text-xs">
                        <button onClick={() => openDoc(doc.id)} className="font-semibold text-[var(--ink)] hover:text-[var(--lala-blue)] hover:underline inline-flex items-center gap-1" data-testid={`mydocs-open-${c.code}`}>
                          <FileText size={12} /> {doc.filename} <ExternalLink size={10} />
                        </button>
                        <div className="text-[var(--ink-soft)] mt-0.5">Subido {new Date(doc.uploaded_at).toLocaleDateString("es-MX")}</div>
                        {doc.evaluator_status && (
                          <div className="mt-2 text-[var(--lala-blue)]">
                            <strong>Revisado por Lala</strong>: {doc.evaluator_status} · {doc.evaluator_score?.toFixed?.(1)}/10
                            {doc.evaluator_notes && <div className="italic">"{doc.evaluator_notes}"</div>}
                          </div>
                        )}
                        <div className="mt-2 text-right">
                          <button
                            onClick={() => fileRefs.current[c.id]?.click()}
                            className="text-[var(--lala-blue)] text-[11px] font-semibold hover:underline"
                            disabled={busy === c.id}
                            data-testid={`mydocs-reupload-${c.code}`}
                          >
                            <RefreshCw size={10} className="inline" /> {busy === c.id ? "Subiendo…" : "Sustituir"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3">
                        <button
                          onClick={() => fileRefs.current[c.id]?.click()}
                          disabled={busy === c.id || readonly}
                          className="btn-primary text-xs w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                          title={readonly ? "Modo Visor activo (Fase 1)" : ""}
                          data-testid={`mydocs-upload-${c.code}`}
                        >
                          <Upload size={12} /> {busy === c.id ? "Subiendo…" : (readonly ? "Modo Visor · Fase 1" : "Subir documento")}
                        </button>
                      </div>
                    )}
                    <input
                      ref={(el) => { fileRefs.current[c.id] = el; }}
                      type="file" hidden
                      accept=".pdf,application/pdf,image/png,image/jpeg,image/webp,image/heic,image/heif"
                      onChange={(e) => upload(c.id, e)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {criteria.length === 0 && (
        <div className="sketch-border bg-[var(--cream-soft)] p-12 text-center text-[var(--ink-soft)]">
          Aún no hay criterios documentales configurados. Pide al equipo Lala que los habilite.
        </div>
      )}
    </div>
  );
}
